import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { getSite, type Site } from '../lib/db';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  createSiteSigninToken, listSiteTokens, revokeToken,
  publicSigninUrl, qrCodeDataUrl,
  type PublicShareToken,
} from '../lib/publicSharing';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

export function SiteSigninQr() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm, alert } = useConfirm();

  const [site, setSite] = useState<Site | null>(null);
  const [tokens, setTokens] = useState<PublicShareToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // QR data URLs keyed by token id. Generated locally with the `qrcode`
  // lib so the sign-in token never leaves the browser. Two sizes per
  // token: 240px for the inline preview, 600px for the "open full size
  // (printable)" link.
  const [qrUrls, setQrUrls] = useState<Record<string, { small: string; large: string }>>({});

  async function refresh() {
    if (!siteId) return;
    setLoading(true);
    try {
      const [s, t] = await Promise.all([getSite(siteId), listSiteTokens(siteId)]);
      setSite(s);
      setTokens(t.filter((x) => x.purpose === 'site_signin'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [siteId]);

  // Generate the QR data URLs whenever the active token list changes.
  // Done in an effect so we don't re-render the encoder on every paint
  // (it's a few ms of CPU per QR).
  useEffect(() => {
    let cancelled = false;
    const active = tokens.filter((t) => t.active && new Date(t.expires_at) > new Date());
    (async () => {
      const next: Record<string, { small: string; large: string }> = {};
      for (const t of active) {
        const url = publicSigninUrl(t.token);
        try {
          const [small, large] = await Promise.all([
            qrCodeDataUrl(url, 240),
            qrCodeDataUrl(url, 600),
          ]);
          next[t.id] = { small, large };
        } catch {
          // Skip a bad token — render falls back to text URL only.
        }
      }
      if (!cancelled) setQrUrls(next);
    })();
    return () => { cancelled = true; };
  }, [tokens]);

  async function generate() {
    if (!org || !siteId) return;
    setCreating(true);
    try {
      await createSiteSigninToken({ orgId: org.org_id, siteId });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    if (!(await confirm({
      title: 'Revoke this link?',
      body: 'Anyone using it will need a new one.',
      confirmLabel: 'Revoke',
      danger: true,
    }))) return;
    try {
      await revokeToken(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function copyUrl(token: string) {
    const url = publicSigninUrl(token);
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      await alert({
        title: "Couldn't copy automatically",
        body: `Copy this URL:\n\n${url}`,
      });
    }
  }

  if (loading) return <AppShell title="Sign-in QR" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  if (!site) return <AppShell title="Sign-in QR" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;

  const active = tokens.filter((t) => t.active && new Date(t.expires_at) > new Date());

  return (
    <AppShell title="Sign-in QR" back={`/sites/${site.id}`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}><strong>{site.name}</strong></p>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Generate a public sign-in URL or QR code. Anyone with the link can sign themselves in
          — useful for cabin tablets and ops who'd rather use their own phone.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {active.length === 0 && (
        <div className="card" style={{ textAlign: 'center', marginBottom: 12 }}>
          <p style={{ margin: '0 0 12px', color: 'var(--ink-soft)' }}>No active sign-in link yet.</p>
          <button type="button" className="btn btn-primary" onClick={generate} disabled={creating}>
            {creating ? 'Generating…' : 'Generate sign-in link'}
          </button>
        </div>
      )}

      {active.map((t) => {
        const url = publicSigninUrl(t.token);
        const qrs = qrUrls[t.id];
        return (
          <div key={t.id} className="card" style={{ marginBottom: 12, textAlign: 'center' }}>
            {qrs ? (
              <img
                src={qrs.small}
                alt="QR code for site sign-in"
                width={240}
                height={240}
                style={{ display: 'block', margin: '0 auto 12px' }}
              />
            ) : (
              <div style={{ width: 240, height: 240, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F5F5', borderRadius: 8, color: 'var(--ink-soft)', fontSize: 13 }}>
                Generating QR…
              </div>
            )}
            <p style={{ wordBreak: 'break-all', fontSize: 12, fontFamily: 'monospace', margin: '0 0 12px' }}>{url}</p>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 12 }}>
              Expires {fmtDate(t.expires_at)} · {t.use_count} use{t.use_count === 1 ? '' : 's'} so far
            </p>
            <div className="btn-stack">
              <button type="button" className="btn btn-primary" onClick={() => copyUrl(t.token)}>
                {copied === t.token ? '✓ Copied' : 'Copy link'}
              </button>
              {qrs && (
                <a
                  className="btn btn-ghost"
                  href={qrs.large}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={`signin-qr-${t.token}.png`}
                >
                  Open QR full size (printable)
                </a>
              )}
              <button type="button" className="btn btn-ghost btn-danger" onClick={() => onRevoke(t.id)}>
                Revoke link
              </button>
            </div>
          </div>
        );
      })}

      {active.length > 0 && (
        <button type="button" className="btn btn-ghost" onClick={generate} disabled={creating}>
          {creating ? 'Generating…' : '+ New link'}
        </button>
      )}

      {tokens.filter((t) => !t.active || new Date(t.expires_at) <= new Date()).length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 22 }}>Old / revoked links</h3>
          {tokens.filter((t) => !t.active || new Date(t.expires_at) <= new Date()).map((t) => (
            <div key={t.id} className="card" style={{ marginBottom: 6, opacity: 0.7 }}>
              <p style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>{t.token}</p>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 11 }}>
                {!t.active ? 'Revoked' : 'Expired'} · {t.use_count} use{t.use_count === 1 ? '' : 's'}
              </p>
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}`)}>← Back to site</button>
      </div>
    </AppShell>
  );
}
